import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  Avatar,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: theme.spacing(8),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(6),
    height: '100vh',
    overflow: 'auto',
  },
}));

const HeroSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInitialView',
})(({ theme, isInitialView }) => ({
  textAlign: 'center',
  transition: 'all 0.5s ease-in-out',
  marginTop: theme.spacing(4),
  marginBottom: isInitialView ? theme.spacing(4) : theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
    marginBottom: isInitialView ? theme.spacing(1) : theme.spacing(0.5),
  },
}));

const ChatContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInitialView',
})(({ theme, isInitialView }) => ({
  height: isInitialView ? '30vh' : '80vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  transition: 'all 0.5s ease-in-out',
  margin: isInitialView ? 'auto 0' : '0',
  padding: theme.spacing(0, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 1),
    width: '100%',
    height: isInitialView ? '50vh' : '65vh',
  },
}));

const MainTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isInitialView',
})(({ theme, isInitialView }) => ({
  transition: 'all 0.5s ease-in-out',
  fontSize: isInitialView ? '4.5rem' : '2.5rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: isInitialView ? '2.5rem' : '1.8rem',
  },
  fontWeight: 800,
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(45deg, #90caf9 30%, #64b5f6 90%)'
      : 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textShadow:
    theme.palette.mode === 'dark'
      ? '2px 2px 4px rgba(255,255,255,0.1)'
      : '2px 2px 4px rgba(0,0,0,0.1)',
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  letterSpacing: '-0.02em',
  textAlign: 'center',
  padding: '0 16px',
}));

const SubTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isInitialView',
})(({ theme, isInitialView }) => ({
  transition: 'all 0.5s ease-in-out',
  opacity: isInitialView ? 1 : 0,
  maxHeight: isInitialView ? '100px' : '0',
  marginTop: isInitialView ? '1.5rem' : '0',
  fontSize: '1.5rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
    marginTop: isInitialView ? '1rem' : '0',
  },
  fontWeight: 400,
  color: theme.palette.mode === 'dark' ? '#b0bec5' : '#546e7a',
  lineHeight: 1.6,
  maxWidth: '800px',
  margin: '0 auto',
  padding: '0 16px',
  textAlign: 'center',
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
}));

const MessagesArea = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  paddingBottom: `calc(${theme.spacing(16)} + 32px)`,
  [theme.breakpoints.down('sm')]: {
    paddingBottom: `calc(${theme.spacing(12)} + 16px)`,
  },
}));

const Message = styled(Box)(({ theme, isBot }) => ({
  display: 'flex',
  justifyContent: isBot ? 'flex-start' : 'flex-end',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  maxWidth: '85%',
  marginLeft: isBot ? 0 : 'auto',
  marginRight: isBot ? 'auto' : 0,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    maxWidth: '95%',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
}));

const MessageBubble = styled(Paper)(({ theme, isBot }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2),
  backgroundColor: isBot
    ? theme.palette.mode === 'dark'
      ? 'rgba(144, 202, 249, 0.08)'
      : 'rgba(33, 150, 243, 0.08)'
    : theme.palette.mode === 'dark'
      ? '#3d3d3d'
      : '#fff',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0,0,0,0.2)'
      : '0 2px 8px rgba(0,0,0,0.05)',
  maxWidth: '100%',
}));

const OptionsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInitialView',
})(({ theme, isInitialView }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f8fa',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
  borderRadius: '20px',
  position: 'fixed',
  bottom: isInitialView ? '30%' : theme.spacing(2),
  left: '50%',
  transform: 'translateX(-50%)',
  width: 'calc(100% - 32px)',
  maxWidth: '900px',
  zIndex: 1000,
  transition: 'all 0.5s ease-in-out',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 -4px 12px rgba(0,0,0,0.3)'
      : '0 -4px 12px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(1),
    padding: theme.spacing(1),
    width: 'calc(100% - 16px)',
  },
}));

const getPublicPath = (path) => {
  return (process.env.PUBLIC_URL || '') + path;
};

const HomePage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [isInitialView, setIsInitialView] = useState(true);
  const messagesEndRef = useRef(null);
  const [currentOptions, setCurrentOptions] = useState('initial');
  const [messages, setMessages] = useState([]);
  const options = {
    initial: [
      { id: 1, text: t('home.optionWhat'), icon: '❓', next: 'what' },
      { id: 2, text: t('home.optionHow'), icon: '⚙️', next: 'how' },
      { id: 3, text: t('home.optionWhy'), icon: '🎯', next: 'why' },
    ],
    what: [
      { id: 4, text: t('home.optionChat'), icon: '💬', next: 'chat' },
      { id: 5, text: t('home.optionExample'), icon: '👀', next: 'example' },
    ],
    how: [
      { id: 6, text: t('home.optionCreate'), icon: '✍️', next: 'create' },
      { id: 7, text: t('home.optionInteract'), icon: '🤝', next: 'interact' },
    ],
    why: [
      { id: 8, text: t('home.optionCreate'), icon: '✍️', next: 'create' },
      { id: 9, text: t('home.optionExample'), icon: '👀', next: 'example' },
    ],
    chat: [
      { id: 10, text: t('home.optionCreate'), icon: '✍️', next: 'create' },
    ],
    example: [
      { id: 12, text: t('home.optionCreate'), icon: '✍️', next: 'create' },
    ],
    create: [],
    interact: [],
  };
  const responses = {
    what: t('home.what'),
    how: t('home.how'),
    why: t('home.why'),
    chat: t('home.chat'),
    example: t('home.example'),
    create: t('home.create'),
    interact: t('home.interact'),
  };

  useEffect(() => {
    // Add initial message with delay
    const timer = setTimeout(() => {
      setMessages([{ id: 1, text: t('home.initialMessage'), isBot: true }]);
    }, 800); // 800ms delay

    return () => clearTimeout(timer);
  }, []);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleOptionClick = async (option) => {
    setIsInitialView(false);

    // Add user message
    const userMessage = {
      id: messages.length + 1,
      text: option.text,
      isBot: false,
    };
    setMessages((prev) => [...prev, userMessage]);

    // Simulate typing delay and add bot response
    setTimeout(() => {
      const botMessage = {
        id: messages.length + 2,
        text: responses[option.next],
        isBot: true,
      };
      setMessages((prev) => [...prev, botMessage]);

      // If there are no more options, show the sign-up button
      if (!options[option.next]?.length) {
        setCurrentOptions('final');
      } else {
        setCurrentOptions(option.next);
      }
    }, 500);
  };

  return (
    <PageContainer>
      <HeroSection isInitialView={isInitialView}>
        <MainTitle component="h1" gutterBottom isInitialView={isInitialView}>
          Get to Know
          <span
            style={{
              color: theme.palette.mode === 'dark' ? '#90caf9' : '#2196F3',
              fontWeight: 900,
              textShadow:
                theme.palette.mode === 'dark'
                  ? '3px 3px 6px rgba(255,255,255,0.2)'
                  : '3px 3px 6px rgba(33,150,243,0.2)',
            }}
          >
            {' '}
            .me
          </span>
        </MainTitle>
        <SubTitle variant="h5" component="h2" isInitialView={isInitialView}>
          {t('home.descPart1')}{' '}
          <span
            style={{
              color: theme.palette.mode === 'dark' ? '#64b5f6' : '#1976d2',
              fontWeight: 500,
            }}
          >
            {t('home.descPart2')}
          </span>
          .{t('home.descPart3')}{' '}
          <span
            style={{
              color: theme.palette.mode === 'dark' ? '#64b5f6' : '#1976d2',
              fontWeight: 500,
            }}
          >
            {t('home.descPart4')}
          </span>
          .
        </SubTitle>
      </HeroSection>

      <Container
        maxWidth="md"
        sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <ChatContainer isInitialView={isInitialView}>
          <MessagesArea>
            {messages.map((message) => (
              <Message key={message.id} isBot={message.isBot}>
                {message.isBot && (
                  <Avatar
                    src={getPublicPath('/man_ai.jpg')}
                    alt="AI Assistant"
                    sx={{
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                    }}
                  />
                )}
                <MessageBubble isBot={message.isBot}>
                  <Typography variant="body1">{message.text}</Typography>
                </MessageBubble>
                {!message.isBot && (
                  <Avatar
                    src={getPublicPath('/woman_ai.jpg')}
                    alt="User"
                    sx={{
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                    }}
                  />
                )}
              </Message>
            ))}
            <div ref={messagesEndRef} style={{ height: 1 }} />
          </MessagesArea>

          <OptionsContainer isInitialView={isInitialView}>
            {currentOptions === 'initial' && (
              <Stack
                direction="row"
                spacing={1}
                flexWrap="nowrap"
                gap={1}
                sx={{
                  maxWidth: '900px',
                  width: '100%',
                  margin: '0 auto',
                  position: 'relative',
                  padding: '0 8px',
                  justifyContent: 'center',
                  '& .MuiButton-root:nth-of-type(1)': {
                    animation: 'fadeInUp 0.5s ease-out',
                    animationDelay: '0.1s',
                  },
                  '& .MuiButton-root:nth-of-type(2)': {
                    animation: 'fadeInUp 0.5s ease-out',
                    animationDelay: '0.2s',
                  },
                  '& .MuiButton-root:nth-of-type(3)': {
                    animation: 'fadeInUp 0.5s ease-out',
                    animationDelay: '0.3s',
                  },
                  '@keyframes fadeInUp': {
                    from: {
                      opacity: 0,
                      transform: 'translateY(20px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                {options[currentOptions].map((option) => (
                  <Button
                    key={option.id}
                    variant={option.id === 1 ? 'contained' : 'outlined'}
                    onClick={() => handleOptionClick(option)}
                    sx={{
                      borderRadius: 25,
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '6px 12px', sm: '8px 16px' },
                    }}
                  >
                    <span style={{ marginRight: '8px', fontSize: '1.1em' }}>
                      {option.icon}
                    </span>
                    {option.text}
                  </Button>
                ))}
              </Stack>
            )}
            {currentOptions !== 'initial' && currentOptions !== 'final' && (
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                gap={1}
                sx={{
                  maxWidth: '900px',
                  width: '100%',
                  margin: '0 auto',
                  position: 'relative',
                  padding: '0 8px',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: '-10px',
                    bottom: '-10px',
                    background:
                      theme.palette.mode === 'dark'
                        ? 'linear-gradient(to bottom, rgba(30,30,30,0), rgba(30,30,30,1) 20%)'
                        : 'linear-gradient(to bottom, rgba(245,248,250,0), rgba(245,248,250,1) 20%)',
                    pointerEvents: 'none',
                    zIndex: 0,
                    borderRadius: '20px',
                  },
                }}
              >
                {options[currentOptions].map((option) => (
                  <Button
                    key={option.id}
                    variant="outlined"
                    onClick={() => handleOptionClick(option)}
                    sx={{ borderRadius: 25 }}
                  >
                    <span style={{ marginRight: '8px', fontSize: '1.1em' }}>
                      {option.icon}
                    </span>
                    {option.text}
                  </Button>
                ))}
              </Stack>
            )}
            {(currentOptions === 'final' ||
              !options[currentOptions]?.length) && (
              <Button
                variant="contained"
                onClick={() => navigate('/signup')}
                sx={{ borderRadius: 25, ml: 'auto' }}
              >
                {t('home.createYourPortfolio')}
              </Button>
            )}
          </OptionsContainer>
        </ChatContainer>
      </Container>
    </PageContainer>
  );
};

export default HomePage;
